import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';
import { toast } from 'react-toastify';
import './discover-sheet.css';
import AddContentButton from '../components/common/AddContentButton';
import ContentCard from '../components/common/ContentCard';
import ContentModal from '../components/common/ContentModal';
import { useUploadContentCustom, useLatestContent } from '../features/content/hooks/useContent';
import { useContentModal } from '../hooks/useContentModal';

const Discover: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  
  // Real content loading
  const { data: contentData, loading: contentLoading, error: contentError, refetch: refetchContent } = useLatestContent(1, 100);
  
  // Upload functionality
  const [isUploading, setIsUploading] = useState(false);
  const [uploadContent] = useUploadContentCustom();
  
  // Content modal functionality
  const { isModalOpen, selectedContent, openModal, closeModal } = useContentModal(
    contentData?.latestContent?.data || []
  );
  
  const handleContentClick = (contentId: string) => {
    const content = contentData?.latestContent?.data?.find(item => item.id === contentId);
    if (content) {
      openModal(content);
    }
  };
  
  // Handle file upload with validation
  const handleFileUpload = async (file: File) => {
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, WebP, HEIC, or GIF.');
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadContent(file, (progress: number) => {
        // Progress tracking - could be used for UI feedback if needed
        Math.min(progress, 99);
      });
      
      if (result.data?.uploadContent?.success) {
        toast.success(result.data.uploadContent.message || 'Content uploaded successfully!');
        
        // Refresh the discover content to show the new upload
        setTimeout(async () => {
          try {
            await refetchContent();
            console.log('Content refreshed after upload');
          } catch (error) {
            console.error('Failed to refresh content:', error);
          }
          setIsUploading(false);
        }, 1000);
      } else {
        const errorMessage = result.data?.uploadContent?.message || 'Upload failed. Please try again.';
        toast.error(errorMessage);
        setIsUploading(false);
      }
    } catch (error) {
      console.error('Error uploading content:', error);
      toast.error('Failed to upload content. Please try again.');
      setIsUploading(false);
    }
  };

  return (
    <div className="relative h-dvh bg-gray-100 pb-20">
      {/* Upload Progress Overlay */}
      {isUploading && (
        <div 
          className="fixed top-0 left-0 w-full h-full z-[9999] flex items-center justify-center"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh'
          }}
        >
          {/* Blurred and darkened background overlay */}
          <div 
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              width: '100%',
              height: '100%'
            }}
          />
          
          {/* Upload progress content */}
          <div 
            className="relative z-10 text-center text-white px-6"
            style={{ zIndex: 10 }}
          >
            {/* Animated upload spinner */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                {/* Outer spinning ring */}
                <svg 
                  className="w-20 h-20 animate-spin" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="url(#uploadSpinnerGradient)"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray="60 40"
                    className="animate-spin"
                  />
                  <defs>
                    <linearGradient id="uploadSpinnerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FF2A2E" />
                      <stop offset="100%" stopColor="#FEB03A" />
                    </linearGradient>
                  </defs>
                </svg>
                
                {/* Inner pulsing circle */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div 
                    className="w-8 h-8 rounded-full animate-pulse"
                    style={{
                      background: 'linear-gradient(45deg, #FF2A2E 0%, #FEB03A 100%)',
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  />
                </div>
                
                {/* Upload icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg 
                    className="w-6 h-6 text-white animate-bounce" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ animationDuration: '3s' }}
                  >
                    <path 
                      d="M12 2L12 8M12 8L9 5M12 8L15 5" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Upload text and progress */}
            <div className="space-y-3">
              <p className="text-xl font-semibold">Uploading content...</p>
              <p className="text-sm text-white/80 max-w-xs mx-auto leading-relaxed">
                Please wait while we process your content
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="px-[8px] pt-[8px] pb-1 md:pb-2">
        {/* Error state */}
        {contentError && (
          <div className="flex items-center justify-center h-64 text-white">
            <p>Failed to load content. Please try again.</p>
          </div>
        )}

        {/* Content grid */}
        {contentData?.latestContent?.data && (
          <div className="grid grid-cols-3 gap-x-[8px] gap-y-[8px]">
            {contentData.latestContent.data.map((content) => (
              <div key={content.id} className="aspect-square">
                <ContentCard 
                  content={content} 
                  onClick={handleContentClick}
                />
              </div>
            ))}
          </div>
        )}
        
        {/* Drag to Explore overlay */}
        <div className="fixed bottom-28 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white py-2 px-6 rounded-full z-20">
          <p className="text-base font-medium">Drag to Explore</p>
        </div>
      </div>
      
      {/* Fixed bottom navigation bar */}
      <div 
        className="fixed bottom-[50px] left-1/2 transform -translate-x-1/2 z-10 h-[64px] max-w-[216px] w-full rounded-[32px]"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.76)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }}
      >
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center justify-between w-full" style={{ paddingLeft: '20px', paddingRight: '20px' }}>
            {/* Discover icon */}
            <button 
              onClick={() => navigate('/discover')}
              className="flex flex-col items-center justify-center bg-transparent border-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path 
                  fillRule="evenodd" 
                  clipRule="evenodd" 
                  d="M1.94503 6.5638C1.49503 7.50297 1.65337 8.60214 1.97003 10.7996L2.2017 12.4138C2.60753 15.2371 2.81087 16.648 3.79003 17.4913C4.7692 18.3346 6.20503 18.3346 9.07753 18.3346H10.9209C13.7934 18.3346 15.2292 18.3346 16.2084 17.4913C17.1875 16.648 17.3909 15.2371 17.7967 12.4138L18.0292 10.7996C18.3459 8.60214 18.5042 7.50297 18.0534 6.5638C17.6025 5.62464 16.6442 5.05297 14.7267 3.9113L13.5725 3.2238C11.8325 2.1863 10.9609 1.66797 9.9992 1.66797C9.03753 1.66797 8.1667 2.1863 6.42587 3.2238L5.2717 3.9113C3.35503 5.05297 2.39587 5.62464 1.94503 6.5638ZM6.8742 15.0013C6.8742 14.8355 6.94005 14.6766 7.05726 14.5594C7.17447 14.4421 7.33344 14.3763 7.4992 14.3763H12.4992C12.665 14.3763 12.8239 14.4421 12.9411 14.5594C13.0584 14.6766 13.1242 14.8355 13.1242 15.0013C13.1242 15.1671 13.0584 15.326 12.9411 15.4432C12.8239 15.5605 12.665 15.6263 12.4992 15.6263H7.4992C7.33344 15.6263 7.17447 15.5605 7.05726 15.4432C6.94005 15.326 6.8742 15.1671 6.8742 15.0013Z" 
                  fill="white"
                />
              </svg>
              <div className="w-10 flex justify-center mt-1">
                <div className="h-1 w-4 bg-white rounded-full"></div>
              </div>
            </button>
            
            {/* ADD CONTENT button */}
            <AddContentButton onFileUpload={handleFileUpload} disabled={isUploading} />
            
            {/* Right icon - Profile navigation */}
            <button 
              onClick={() => navigate(`/profile/${user?.id}`)}
              className="flex flex-col items-center justify-center bg-transparent border-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path 
                  d="M1.66602 10.0013C1.66602 5.3988 5.39685 1.66797 9.99935 1.66797C14.1768 1.66797 17.6368 4.74214 18.2393 8.7513H18.071C15.996 8.7513 14.6327 8.75047 13.4935 9.1488C12.489 9.50026 11.5766 10.0735 10.8241 10.826C10.0716 11.5786 9.4983 12.491 9.14685 13.4955C8.74852 14.6346 8.74852 15.998 8.74935 18.073V18.2413C4.74018 17.638 1.66602 14.1788 1.66602 10.0013Z" 
                  fill="rgba(255, 255, 255, 0.6)"
                />
                <path 
                  d="M17.7017 11.4233L11.4233 17.7017C11.328 17.7965 11.2252 17.8834 11.1158 17.9617C10.7913 18.1979 10.4014 18.3278 10 18.3333C10 17.8733 10 17.4592 10.0025 17.0833C10.0125 15.555 10.065 14.6575 10.3275 13.9067C10.6169 13.0796 11.089 12.3283 11.7086 11.7086C12.3283 11.089 13.0796 10.6169 13.9067 10.3275C14.6567 10.065 15.555 10.0125 17.0833 10.0025C17.4592 10 17.8733 10 18.3333 10C18.3333 10.405 18.1925 10.7883 17.9617 11.1167C17.8834 11.2257 17.7964 11.3283 17.7017 11.4233Z" 
                  fill="rgba(255, 255, 255, 0.6)"
                />
              </svg>
              <div className="w-10 flex justify-center mt-1">
                {/* Empty div for spacing consistency */}
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Loading state overlay with blurred background */}
      {contentLoading && (
        <div 
          className="fixed top-0 left-0 w-full h-full z-[9999] flex items-center justify-center"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh'
          }}
        >
          {/* Blurred and darkened background overlay */}
          <div 
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              width: '100%',
              height: '100%'
            }}
          />
          
          {/* Loading content */}
          <div 
            className="relative z-10 text-center text-white px-6"
            style={{ zIndex: 10 }}
          >
            {/* Loading spinner */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <svg 
                  className="w-16 h-16 animate-spin" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="url(#loadingGradient)"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray="60 40"
                    className="animate-spin"
                  />
                  <defs>
                    <linearGradient id="loadingGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FF2A2E" />
                      <stop offset="100%" stopColor="#FEB03A" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
            
            {/* Loading text */}
            <div className="space-y-3">
              <p className="text-xl font-semibold">Loading content...</p>
              <p className="text-sm text-white/80 max-w-xs mx-auto leading-relaxed">
                Please wait while we fetch the latest posts for you
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content Modal */}
      {selectedContent && (
        <ContentModal
          content={selectedContent}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default Discover; 